import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Calculator, FileText, Building2, ChevronRight, Recycle, TrendingDown, Shield, ArrowRight, Check, Package, Scale } from 'lucide-react';
import Header from '../components/Header';

const Home = () => {
  return (
    <div className="min-h-screen bg-gray-50 font-['Inter',sans-serif]">
      {/* Navbar */}
      <Header />

      {/* Hero */}
      <section className="bg-white pt-8 sm:pt-16 pb-12 sm:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <div className="space-y-6 sm:space-y-8">
              <div className="inline-flex items-center gap-2 bg-green-50 text-[#00C300] text-sm font-semibold px-4 py-2 rounded-full">
                <Recycle className="w-4 h-4" />
                Pakuočių atliekų apskaita
              </div>
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight tracking-tight">
                Taršos mokestis –
                <span className="text-[#00C300]"> paprastai</span>
              </h1>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-lg">
                Visa informacija apie pakuočių atliekų tvarkymą, organizacijų pasirinkimą ir valstybės taršos mokesčio ypatumus vienoje vietoje.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Link to="/skaiciuokle" className="w-full sm:w-auto">
                  <Button size="lg" className="w-full sm:w-auto bg-[#00C300] hover:bg-[#00A300] text-white rounded-full px-8 font-semibold h-12">
                    <Scale className="w-5 h-5 mr-2" />
                    Palyginti kainas
                  </Button>
                </Link>
                <Link to="/register" className="w-full sm:w-auto">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto rounded-full px-8 font-semibold h-12 border-2 border-gray-200 hover:border-[#00C300] hover:text-[#00C300]">
                    Registruotis
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
              <div className="flex flex-wrap items-center gap-4 sm:gap-6 pt-2">
                {['Nemokamas naudojimas', 'PDF ataskaitos', 'Organizacijų palyginimas'].map(f => (
                  <div key={f} className="flex items-center gap-1.5 text-sm text-gray-600">
                    <Check className="w-4 h-4 text-[#00C300]" />
                    {f}
                  </div>
                ))}
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-3xl p-8 shadow-xl">
                <div className="bg-white rounded-2xl shadow-lg p-6 space-y-4">
                  {/* Packaging type header */}
                  <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-4 py-2 mb-2">
                    <Package className="w-4 h-4 text-[#00C300]" />
                    <span className="text-sm font-bold text-gray-700">Stiklinė pakuotė – 1 000 kg (1 tona)</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Valstybės taršos mokestis</span>
                    <span className="text-2xl font-extrabold text-gray-900">€279.00</span>
                  </div>
                  <div className="h-px bg-gray-100" />
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Su organizacija</span>
                    <span className="text-2xl font-extrabold text-[#00C300]">€133.00</span>
                  </div>
                  <div className="bg-green-50 rounded-xl p-3 flex items-center justify-between">
                    <span className="text-sm font-semibold text-green-800">Sutaupymas</span>
                    <span className="text-lg font-bold text-green-700">€146.00 (52%)</span>
                  </div>
                  <div className="space-y-2">
                    {[
                      { name: 'Gamtos Ateitis', price: '€133.00', best: true },
                      { name: 'Žaliasis taškas', price: '€193.20', best: false },
                    ].map((org) => (
                      <div key={org.name} className="flex items-center justify-between p-2 rounded-lg bg-gray-50">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${org.best ? 'bg-[#00C300]' : 'bg-gray-300'}`} />
                          <span className="text-sm text-gray-700 font-medium">{org.name}</span>
                          {org.best && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">Geriausia</span>}
                        </div>
                        <span className="text-sm font-semibold text-gray-900">{org.price}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 text-center">* 2026 m. oficialūs tarifai €/t, tvarkoma tona</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-14">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-4">Kodėl svarbu teisingai tvarkyti pakuočių atliekas?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-base sm:text-lg">Tinkama apskaita padeda sumažinti kaštus ir prisideda prie aplinkos apsaugos.</p>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
            {[
              { icon: TrendingDown, color: 'bg-green-100 text-[#00C300]', title: 'Mažesni kaštai', desc: 'Pasirinkus tinkamą organizaciją, galite sumažinti mokesčius iki 40% lyginant su valstybės tarifu.' },
              { icon: Shield, color: 'bg-blue-100 text-blue-600', title: 'Teisinis saugumas', desc: 'Laikydamiesi visų reikalavimų, išvengsite baudų ir užtikrinsite atitiktį Lietuvos teisės aktams.' },
              { icon: Recycle, color: 'bg-emerald-100 text-emerald-600', title: 'Aplinkos apsauga', desc: 'Prisidėkite prie švaresnės Lietuvos ir mažesnio poveikio aplinkai.' },
            ].map(({ icon: Icon, color, title, desc }) => (
              <Card key={title} className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <div className={`w-12 sm:w-14 h-12 sm:h-14 ${color} rounded-2xl flex items-center justify-center mb-4`}>
                    <Icon className="w-6 sm:w-7 h-6 sm:h-7" />
                  </div>
                  <CardTitle className="text-lg sm:text-xl font-bold text-gray-900">{title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Organizations */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <div className="space-y-6">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900">Pakuočių tvarkymo organizacijos</h2>
              <p className="text-gray-600 leading-relaxed text-base sm:text-lg">Lietuvoje veikia kelios pakuočių tvarkymo organizacijos, kurios padeda įmonėms įvykdyti pakuočių perdirbimo įsipareigojimus.</p>
              <div className="space-y-4 sm:space-y-5">
                {[
                  { icon: Building2, title: 'Organizacijų pasirinkimas', desc: 'Pasirinkite tarp kolektyvinio tvarkymo organizacijų arba individualiai vykdykite įsipareigojimus.' },
                  { icon: ChevronRight, title: 'Valstybės taršos mokestis', desc: 'Jei nesate prisijungę prie organizacijos, privalote mokėti valstybės taršos mokestį.' },
                ].map(({ icon: Icon, title, desc }) => (
                  <div key={title} className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4 rounded-2xl hover:bg-gray-50 transition-colors">
                    <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-[#00C300]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">{title}</h3>
                      <p className="text-gray-600 text-sm sm:text-base">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gray-50 rounded-3xl p-4 sm:p-8 hidden lg:block">
              <img
                src="https://images.pexels.com/photos/3735215/pexels-photo-3735215.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Pakuočių tvarkymas"
                className="rounded-2xl shadow-xl w-full h-[350px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-20 bg-[#00C300]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-4xl font-extrabold text-white mb-4">Apskaičiuokite taršos mokestį dabar</h2>
          <p className="text-green-50 text-base sm:text-lg mb-6 sm:mb-10 max-w-2xl mx-auto">Užsiregistruokite ir naudokite išplėstą skaičiuotuvą – PDF ataskaitos, organizacijų palyginimas ir daugiau!</p>
          <Link to="/register">
            <Button size="lg" className="w-full sm:w-auto bg-white text-[#00C300] hover:bg-gray-50 rounded-full px-8 sm:px-10 font-bold h-12 sm:h-14 text-base sm:text-lg shadow-lg">
              <Calculator className="w-5 h-5 mr-2" />
              Registruotis nemokamai
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-10 sm:py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 sm:gap-10 mb-8 sm:mb-10">
            <div className="col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <Recycle className="w-6 sm:w-7 h-6 sm:h-7 text-[#00C300]" />
                <span className="text-white font-bold text-base sm:text-lg">taršosmokestis.lt</span>
              </div>
              <p className="text-gray-400 leading-relaxed text-sm sm:text-base">Informacijos portalas apie pakuočių atliekų apskaitą ir taršos mokestį Lietuvoje.</p>
            </div>
            <div>
              <h3 className="text-white font-bold mb-3 sm:mb-4 text-sm sm:text-base">Nuorodos</h3>
              <ul className="space-y-2 sm:space-y-3 text-sm">
                <li><Link to="/" className="hover:text-[#00C300] transition-colors">Pradžia</Link></li>
                <li><Link to="/skaiciuokle" className="hover:text-[#00C300] transition-colors">Skaičiuoklė</Link></li>
                <li><Link to="/register" className="hover:text-[#00C300] transition-colors">Registracija</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-bold mb-3 sm:mb-4 text-sm sm:text-base">Kontaktai</h3>
              <p className="text-gray-400 leading-relaxed text-sm">info@tarsosmokestis.lt<br />+370 600 00000</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-6 sm:pt-8 text-center text-gray-500 text-xs sm:text-sm">
            © {new Date().getFullYear()} taršosmokestis.lt – Visos teisės saugomos
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
